import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext(null);

const INACTIVITY_WARNING_MS = 15 * 60 * 1000;
const INACTIVITY_LOGOUT_MS  = 20 * 60 * 1000;
const BASE = process.env.REACT_APP_API_URL || '/api';

export function AuthProvider({ children }) {
  const [user,            setUser]            = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading,         setLoading]         = useState(true);

  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const warningTimer = useRef(null);
  const logoutTimer = useRef(null);
  const lastActivity = useRef(Date.now());

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const saved = localStorage.getItem('admin_user');
    if (token && saved) {
      try {
        const u = JSON.parse(saved);
        if (u.role === 'system_admin') {
          setUser(u);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_refresh_token');
          localStorage.removeItem('admin_user');
        }
      } catch {}
    }
    setLoading(false);
  }, []);

  const login = (token, userData, refreshToken) => {
    if (userData.role !== 'system_admin') {
      throw new Error('System Admin access required');
    }
    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin_user', JSON.stringify(userData));
    if (refreshToken) localStorage.setItem('admin_refresh_token', refreshToken);
    setUser(userData);
    setIsAuthenticated(true);
    resetTimers();
  };

  const logout = useCallback(async () => {
    try {
      const rt = localStorage.getItem('admin_refresh_token');
      if (rt) {
        await fetch(`${BASE}/admin/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: rt }),
        });
      }
    } catch { /* best-effort */ }
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_user');
    clearTimeout(warningTimer.current);
    clearTimeout(logoutTimer.current);
    setUser(null);
    setIsAuthenticated(false);
    setShowTimeoutWarning(false);
  }, []);

  // ── Inactivity timers ─────────────────────────────────────────────────────
  const resetTimers = useCallback(() => {
    lastActivity.current = Date.now();
    setShowTimeoutWarning(false);
    clearTimeout(warningTimer.current);
    clearTimeout(logoutTimer.current);
    if (!localStorage.getItem('admin_token')) return;
    warningTimer.current = setTimeout(() => setShowTimeoutWarning(true), INACTIVITY_WARNING_MS);
    logoutTimer.current  = setTimeout(() => logout(), INACTIVITY_LOGOUT_MS);
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const onActivity = () => {
      if (Date.now() - lastActivity.current > 30000) resetTimers();
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    resetTimers();
    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      clearTimeout(warningTimer.current);
      clearTimeout(logoutTimer.current);
    };
  }, [isAuthenticated, resetTimers]);

  const dismissTimeoutWarning = useCallback(() => {
    setShowTimeoutWarning(false);
    resetTimers();
  }, [resetTimers]);

  return (
    <AuthContext.Provider value={{
      user, isAuthenticated, loading,
      login, logout,
      showTimeoutWarning, dismissTimeoutWarning,
    }}>
      {children}

      {showTimeoutWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--bg-card, #1a2332)', borderRadius: '12px',
            padding: '32px', maxWidth: '400px', width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center',
            border: '1px solid var(--border, #2a3a4a)',
          }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: 'rgba(234,179,8,0.1)', margin: '0 auto 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                stroke="#eab308" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary, #e2e8f0)' }}>
              Session Expiring Soon
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: '0.88rem', lineHeight: 1.5, color: 'var(--text-secondary, #94a3b8)' }}>
              You have been inactive for 15 minutes. Your admin session will end in 5 minutes for security.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={logout} style={{
                padding: '10px 20px', borderRadius: '8px', border: '1px solid var(--border, #2a3a4a)',
                background: 'transparent', color: 'var(--text-secondary, #94a3b8)',
                fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit',
              }}>Log Out Now</button>
              <button onClick={dismissTimeoutWarning} style={{
                padding: '10px 24px', borderRadius: '8px', border: 'none',
                background: 'var(--blue, #3b82f6)', color: '#fff',
                fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit',
              }}>Continue Session</button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
